<template>
    <loading v-if="loading"></loading>
    <div class="ai-prioritization" v-else>

        <b-card class="mb-4">
            <p>
                Welcome to AI-Powered Prioritization! This tool uses GPT-4 to help you prioritize your top posts based on your <a href="/admin/product_vision">Product Vision Board</a>. It analyzes user interactions such as vote counts to provide you with insightful recommendations.
            </p>
            <p>
                To get started, you can customize the statuses to be considered for prioritization. Then, click the "Prioritize with AI" button to generate your top 5.
            </p>
        </b-card>

        <b-alert v-if="!visionAvailable" show variant="warning">
            Before prioritizing, please set up your <a href="/admin/product_vision">Product Vision Board</a> (30 seconds with AI).
        </b-alert>

        <div class="d-flex justify-content-between">
            <div>
                <b-button @click="prioritizeWithAI" variant="primary" class="mb-3 ml-2" :disabled="loadingAi || !visionAvailable">
                    <b-icon icon="stars"></b-icon> Prioritize with AI
                </b-button>
            </div>
            <div>
                <b-button @click="$bvModal.show('edit-statuses-modal')" variant="outline" class="mb-3">
                    Edit Statuses
                </b-button>
            </div>
        </div>

        <b-modal id="edit-statuses-modal" title="Edit Prioritization Statuses" @ok="saveStatuses" cancel-variant="outline">
            <b-form-group label="Select statuses for prioritization:">
                <b-form-checkbox-group v-model="editingStatuses" :options="statusOptions"></b-form-checkbox-group>
            </b-form-group>
        </b-modal>

        <loading v-if="loadingAi"></loading>

        <!-- Winners Podium -->
        <div v-if="prioritizedPosts.length > 0" class="winners-podium mb-4">
            <h3 class="text-center mb-4">Winners Podium</h3>
            <div class="d-flex justify-content-center align-items-end">
                <!-- Second Place -->
                <div class="podium-item mx-2" v-if="prioritizedPosts[1]">
                    <div class="podium-content second-place">
                        <div class="rank">2</div>
                        <div class="title">{{ prioritizedPosts[1].title }}</div>
                        <div class="votes">
                            <b-icon icon="hand-thumbs-up"></b-icon> {{ prioritizedPosts[1].votes_count }}
                        </div>
                    </div>
                    <div class="podium-base second-place-base"></div>
                </div>

                <!-- First Place -->
                <div class="podium-item mx-2" v-if="prioritizedPosts[0]">
                    <div class="podium-content first-place">
                        <div class="rank">1</div>
                        <div class="title">{{ prioritizedPosts[0].title }}</div>
                        <div class="votes">
                            <b-icon icon="hand-thumbs-up"></b-icon> {{ prioritizedPosts[0].votes_count }}
                        </div>
                    </div>
                    <div class="podium-base first-place-base"></div>
                </div>

                <!-- Third Place -->
                <div class="podium-item mx-2" v-if="prioritizedPosts[2]">
                    <div class="podium-content third-place">
                        <div class="rank">3</div>
                        <div class="title">{{ prioritizedPosts[2].title }}</div>
                        <div class="votes">
                            <b-icon icon="hand-thumbs-up"></b-icon> {{ prioritizedPosts[2].votes_count }}
                        </div>
                    </div>
                    <div class="podium-base third-place-base"></div>
                </div>
            </div>
        </div>

        <b-table v-if="prioritizedPosts.length > 0" :items="prioritizedPosts" :fields="fields" striped hover class="custom-table">
            <template #cell(rank)="data">
                <div class="rank-circle" :class="getRankClass(data.value)">
                    {{ data.value }}
                </div>
            </template>
            <template #cell(title)="data">
                <div class="d-flex align-items-center">
                    <b-icon :icon="data.item.icon" class="mr-2"></b-icon>
                    <b-link :href="data.item.url" class="d-flex align-items-center">
                        {{ data.item.title }}
                    </b-link>
                </div>
            </template>
            <template #cell(explanation)="data">
                <div class="d-flex align-items-center">
                    <b-icon icon="info-circle" class="mr-2 text-muted"></b-icon>
                    {{ data.value }}
                </div>
            </template>
            <template #cell(votes_count)="data">
                <div class="d-flex align-items-center">
                    <b-icon icon="hand-thumbs-up" class="mr-2 text-primary"></b-icon>
                    {{ data.value }}
                </div>
            </template>
        </b-table>

        <b-alert v-else show variant="info">
            Click "Prioritize with AI" to generate your top 5 prioritized posts.
        </b-alert>
    </div>
</template>

<script>
import NotificationPop from "../mixins/NotificationPop";
import Loading from "./Loading";

export default {
    name: 'AIPrioritization',
    mixins: [NotificationPop],
    components: {
        Loading
    },
    data() {
        return {
            loading: false,
            loadingAi: false,
            editingStatuses: [],
            selectedStatuses: [],
            statusOptions: [],
            prioritizedPosts: [],
            visionAvailable: false,
            fields: [
                { key: 'rank', label: '#', class: 'text-left' },
                { key: 'title', label: 'Post', class: 'text-left' },
                { key: 'explanation', label: 'AI Insight', class: 'text-left' },
                { key: 'votes_count', label: 'Votes', class: 'text-left' }
            ]
        }
    },
    created() {
        this.loadAll();
    },
    methods: {
        async loadAll() {
            try {
                await Promise.all([
                    this.fetchVision(),
                    this.fetchSettings(),
                    this.fetchStatuses()
                ]);
            } catch (error) {
                console.error('An error occurred while fetching data:', error);
            } finally {
                this.loading = false;
            }
        },
        getRankClass(rank) {
            const classes = ['bg-gold', 'bg-silver', 'bg-bronze', 'bg-blue', 'bg-purple'];
            return classes[rank - 1] || 'bg-gray';
        },
        async fetchSettings() {
            return this.$http.get('/http_api/prioritization-settings')
                .then((response) => {
                    this.prioritizationFramework = response.data.framework;
                    this.selectedStatuses = response.data.statuses || []; // Initialize as empty array if null
                    this.editingStatuses = [...this.selectedStatuses];
                })
                .catch((error) => {
                    this.popNotification('Failed to fetch prioritization settings.', 'danger');
                });
        },
        async fetchVision() {
            return this.$http.get('/http_api/product-vision')
                .then(response => {
                    this.visionAvailable = true;
                })
                .catch(error => {
                    if (error.response && error.response.status === 404) {
                        // Vision not found, set to editing mode
                        this.visionAvailable = false;
                    } else {
                        // Other error occurred
                        this.popNotification('Failed to fetch product vision.', 'danger');
                        console.error('Error fetching product vision:', error);
                    }
                });
        },
        async fetchStatuses() {
            return this.$http.get(`/http_api/statuses`)
                .then((response) => {
                    this.statusOptions = response.data.data.map(status => ({
                        value: status.id,
                        text: status.name,
                    }));
                    this.prioritizationStatuses = response.data.data.filter(status => status.is_prioritization_status);
                })
                .catch((error) => {
                    this.popNotification('Failed to fetch statuses.', 'danger');
                });
        },
        saveStatuses() {
            this.selectedStatuses = this.editingStatuses;
            this.updateSettings();
        },
        updateSettings() {
            let settings = {
                statuses: this.selectedStatuses,
                framework: this.prioritizationFramework
            }
            this.$http.post('/http_api/prioritization-settings', settings)
                .then((response) => {
                    this.selectedStatuses = response.data.statuses;
                    this.popNotification('Prioritization settings updated successfully.', 'success');
                })
                .catch((error) => {
                    this.popNotification('Failed to update prioritization settings.', 'danger');
                });
        },
        prioritizeWithAI() {
            this.loadingAi = true;
            this.$http.post('/http_api/prioritize-ai')
                .then((response) => {
                    this.prioritizedPosts = response.data.map((post, index) => ({
                        rank: index + 1,
                        ...post  // This spreads all the post properties returned from the backend
                    }));
                    this.popNotification('AI prioritization completed successfully.', 'success');
                })
                .catch((error) => {
                    if (error.response && error.response.status === 403) {
                        this.popNotification('Not enough AI credits to perform prioritization.', 'warning');
                    } else {
                        this.popNotification('Failed to perform AI prioritization.', 'danger');
                    }
                })
                .finally(() => {
                    this.loadingAi = false;
                });
        }
    }
}
</script>
<style scoped>
.custom-table {
    /*box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);*/
    /*border-radius: 8px;*/
    overflow: hidden;
}
.custom-table thead th {
    background-color: #f3f4f6; /* gray-100 */
    border-top: none;
}
.custom-table td {
    vertical-align: middle;
}
.rank-circle {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1f2937; /* gray-800 for better contrast */
    font-weight: bold;
}
.bg-gold { background-color: #fef3c7; } /* yellow-100 */
.bg-silver { background-color: #f3f4f6; } /* gray-100 */
.bg-bronze { background-color: #FED7AA; } /* orange-200 */
.bg-blue { background-color: #E7E5E4; } /* blue-200 */
.bg-purple { background-color: #D6D3D1; } /* purple-200 */
.bg-gray { background-color: #e5e7eb; } /* gray-200 */

.winners-podium {
    /*background-color: #f9fafb; !* gray-50 *!*/
    /*border-radius: 8px;*/
    padding: 20px;
    /*box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);*/
}

.podium-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 150px;
}

.podium-content {
    width: 100%;
    padding: 10px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    color: #1f2937; /* gray-800 for better contrast */
    text-align: center;
}

.podium-base {
    width: 100%;
}

.first-place {
    background-color: #fef3c7; /* yellow-100 */
    height: 120px;
}

.second-place {
    background-color: #f3f4f6; /* gray-100 */
    height: 100px;
}

.third-place {
    background-color: #FED7AA; /* orange-200 */
    height: 80px;
}

.first-place-base {
    background-color: #fef3c7; /* yellow-100 */
    height: 60px;
}

.second-place-base {
    background-color: #f3f4f6; /* gray-100 */
    height: 40px;
}

.third-place-base {
    background-color: #FED7AA; /* orange-200 */
    height: 20px;
}

.rank {
    font-size: 24px;
    font-weight: bold;
}

.title {
    font-size: 14px;
    margin-top: 5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.votes {
    font-size: 12px;
    margin-top: 5px;
}
</style>